import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, Check, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

/**
 * ShareButtons — expandable share menu with Twitter/X, Facebook, WhatsApp,
 * Copy Link, and Native Share (mobile only).
 *
 * Props
 * -----
 * url        (string, required) — canonical URL to share
 * title      (string, required) — page title, used for prefilled tweets and copy
 * text       (string, optional) — extra prefilled message body (e.g. a headline)
 * eventName  (string, optional) — analytics event prefix, defaults to "share"
 * variant    ("compact" | "row", default "compact") — compact = single button
 *            that expands to a popover; row = always-visible row of icons.
 */
export default function ShareButtons({
  url,
  title,
  text = "",
  eventName = "share",
  variant = "compact",
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  // Close popover when clicking outside
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    document.addEventListener("touchstart", handle);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("touchstart", handle);
    };
  }, [open]);

  const encUrl = encodeURIComponent(url);
  const encText = encodeURIComponent(text || title);
  const encTitle = encodeURIComponent(title);
  // Combined text + url for platforms that don't have separate url params
  const encTextAndUrl = encodeURIComponent(`${text || title} — ${url}`);

  const targets = [
    {
      key: "twitter",
      label: "X / Twitter",
      href: `https://twitter.com/intent/tweet?text=${encText}&url=${encUrl}&via=timecapsule24`,
      color: "#000000",
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M18.244 2H21l-6.52 7.45L22 22h-6.828l-4.77-6.242L4.8 22H2l7.05-8.06L2 2h6.828l4.294 5.68L18.244 2Zm-1.196 18h1.813L7.03 4H5.11l11.938 16Z"/>
        </svg>
      ),
    },
    {
      key: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${encText}`,
      color: "#1877F2",
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.891 3.777-3.891 1.094 0 2.238.196 2.238.196v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.99C18.343 21.128 22 16.991 22 12Z"/>
        </svg>
      ),
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      href: `https://api.whatsapp.com/send?text=${encTextAndUrl}`,
      color: "#25D366",
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M20.52 3.48A11.87 11.87 0 0 0 12.06 0C5.55 0 .27 5.28.27 11.8c0 2.08.54 4.11 1.57 5.9L.17 24l6.45-1.68a11.78 11.78 0 0 0 5.44 1.38h.01c6.51 0 11.79-5.28 11.79-11.8 0-3.16-1.23-6.12-3.34-8.42Zm-8.46 18.16h-.01a9.83 9.83 0 0 1-5.02-1.37l-.36-.21-3.83 1 1.02-3.73-.24-.38a9.84 9.84 0 0 1-1.51-5.25c0-5.43 4.42-9.85 9.85-9.85 2.63 0 5.1 1.03 6.95 2.89a9.79 9.79 0 0 1 2.89 6.96c0 5.44-4.42 9.86-9.85 9.86Zm5.4-7.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15s-.77.97-.94 1.17c-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.65-2.05-.17-.3-.02-.47.13-.62.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.11 3.22 5.11 4.52.71.31 1.27.49 1.71.63.72.23 1.37.2 1.88.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z"/>
        </svg>
      ),
    },
  ];

  const openTarget = (t) => {
    trackEvent(`${eventName}_click`, { channel: t.key, url });
    // Open in a small popup — feels more native than a full tab for shares
    const w = 640;
    const h = 520;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    window.open(
      t.href,
      "share_" + t.key,
      `width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`,
    );
    setOpen(false);
  };

  const copyLink = async () => {
    trackEvent(`${eventName}_click`, { channel: "copy", url });
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — long-press the URL bar to copy");
    }
  };

  const nativeShare = async () => {
    trackEvent(`${eventName}_click`, { channel: "native", url });
    try {
      await navigator.share({ url, title, text });
      setOpen(false);
    } catch {
      /* user cancelled */
    }
  };

  const hasNative =
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function";

  if (variant === "row") {
    return (
      <div
        className="flex items-center gap-2 flex-wrap"
        data-testid="share-buttons-row"
      >
        {targets.map((t) => (
          <button
            key={t.key}
            onClick={() => openTarget(t)}
            className="inline-flex items-center gap-1.5 border-2 border-ink px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:bg-ink hover:text-paper"
            data-testid={`share-${t.key}-btn`}
            title={`Share on ${t.label}`}
            style={{ transition: "background-color 150ms, color 150ms" }}
          >
            {t.icon} {t.label}
          </button>
        ))}
        <button
          onClick={copyLink}
          className="inline-flex items-center gap-1.5 border-2 border-ink px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:bg-ink hover:text-paper"
          data-testid="share-copy-btn"
          title="Copy link"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
        {hasNative ? (
          <button
            onClick={nativeShare}
            className="inline-flex items-center gap-1.5 border-2 border-ink px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:bg-ink hover:text-paper"
            data-testid="share-native-btn"
            title="More share options"
          >
            <Share2 size={12} /> More
          </button>
        ) : null}
      </div>
    );
  }

  // Compact — single button that expands into a popover
  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        onClick={() => {
          // On mobile with native share, skip the popover entirely
          if (hasNative && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
            nativeShare();
            return;
          }
          setOpen((v) => !v);
        }}
        className="inline-flex items-center gap-1.5 border-2 border-ink px-3 py-2 font-mono uppercase tracking-widest text-[10px] sm:text-xs hover:bg-ink hover:text-paper"
        data-testid="share-toggle-btn"
        title="Share this page"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Share2 size={12} /> <span className="hidden sm:inline">Share</span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 border-2 border-ink bg-paper shadow-hard z-50"
          data-testid="share-popover"
        >
          <div className="flex items-center justify-between border-b-2 border-ink px-3 py-2 bg-paper-alt">
            <div className="font-mono uppercase tracking-widest text-[10px]">Share</div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 hover:text-press-red"
              data-testid="share-close-btn"
              aria-label="Close"
            >
              <X size={12} />
            </button>
          </div>
          <ul className="py-1">
            {targets.map((t) => (
              <li key={t.key}>
                <button
                  onClick={() => openTarget(t)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left font-mono uppercase tracking-widest text-[11px] hover:bg-paper-alt"
                  data-testid={`share-${t.key}-btn`}
                  role="menuitem"
                >
                  <span
                    className="inline-flex items-center justify-center w-5 h-5"
                    style={{ color: t.color }}
                  >
                    {t.icon}
                  </span>
                  {t.label}
                </button>
              </li>
            ))}
            <li className="border-t border-dashed border-ink/40 mt-1 pt-1">
              <button
                onClick={copyLink}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left font-mono uppercase tracking-widest text-[11px] hover:bg-paper-alt"
                data-testid="share-copy-btn"
                role="menuitem"
              >
                <span className="inline-flex items-center justify-center w-5 h-5">
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                </span>
                {copied ? "Copied!" : "Copy link"}
              </button>
            </li>
          </ul>
        </div>
      ) : null}
    </div>
  );
}
